'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from '@/i18n/navigation'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bell,
  CheckCircle,
  Clock,
  MessageSquare,
  Monitor,
  Phone,
  RefreshCw,
  Smartphone,
  User,
  UserCheck,
  UserX,
  XCircle,
  Globe,
} from 'lucide-react'
import { api } from '@/lib/api'
import type { HandoverRequest } from '@/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useNotificationActions } from '@/lib/notifications'
import { buildAgentDashboardChatUrl, formatRelativeTime } from '@/lib/utils'
import { derivePrimaryTeamId, normalizeTeamIds } from '@/lib/utils/teams'
import { Badge } from '@/components/ui/badge'
import { useAgentAuth } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { buildPermissionSet, hasPermission } from '@/lib/permissions'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface HandoverNotificationsProps {
  className?: string
  onUpdate?: () => void
  showPendingAssignments?: boolean
  showHistory?: boolean
  teamId?: number | null
  mode?: 'assignments' | 'pendingRequests'
}

interface ResponseModalData {
  request: HandoverRequest
  response: 'accepted' | 'rejected'
  reason: string
}

type HistoryFilterValue = 'teams' | 'me' | `team:${number}`

type HistoryQueryOptions =
  | { scope: 'me' }
  | { scope: 'teams' }
  | { scope: 'team'; teamId: number }

const resolveTeamMeta = (
  source: Partial<Record<'teamIds' | 'primaryTeamId' | 'teamId', unknown>>
) => {
  const primaryFallback =
    typeof source.primaryTeamId === 'number'
      ? source.primaryTeamId
      : source.teamId

  const teamIds = normalizeTeamIds(source.teamIds, primaryFallback)
  const primaryTeamId = derivePrimaryTeamId(teamIds, primaryFallback)

  return {
    teamIds,
    primaryTeamId,
  }
}

const getPlatformLabel = (platform?: string | null) => {
  if (!platform) return 'Web'
  switch (platform) {
    case 'whatsapp':
      return 'WhatsApp'
    case 'facebook':
      return 'Facebook'
    case 'instagram':
      return 'Instagram'
    case 'telegram':
      return 'Telegram'
    case 'wechat':
      return 'WeChat'
    case 'line':
      return 'LINE'
    case 'web':
      return 'Web'
    default:
      return platform.charAt(0).toUpperCase() + platform.slice(1)
  }
}

const getPlatformIcon = (platform?: string | null) => {
  switch (platform) {
    case 'whatsapp':
      return <Phone className="h-3 w-3" />
    case 'facebook':
      return <Smartphone className="h-3 w-3" />
    default:
      return <Globe className="h-3 w-3" />
  }
}

export function HandoverNotifications({
  className = '',
  onUpdate,
  showPendingAssignments = true,
  showHistory = true,
  teamId = null,
  mode = 'assignments',
}: HandoverNotificationsProps) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { showSuccess } = useNotificationActions()
  const [error, setError] = useState<string | null>(null)
  const [showResponseModal, setShowResponseModal] = useState(false)
  const [responseModalData, setResponseModalData] =
    useState<ResponseModalData | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showTeamSelectionModal, setShowTeamSelectionModal] = useState(false)
  const [selectedTeamForAccept, setSelectedTeamForAccept] = useState<
    number | null
  >(null)
  const [pendingAcceptRequest, setPendingAcceptRequest] =
    useState<HandoverRequest | null>(null)

  const { user } = useAgentAuth()
  const { teamFilter, fetchTeams } = useTeamsStore()
  const availableTeams = useMemo(
    () => teamFilter.availableTeams ?? [],
    [teamFilter.availableTeams]
  )

  const permissionSet = useMemo(
    () => buildPermissionSet(user?.permissions),
    [user?.permissions]
  )

  const canRespondToTransfers = useMemo(
    () => hasPermission(permissionSet, 'CHAT_TRANSFER'),
    [permissionSet]
  )

  const agentTeamIds = useMemo(
    () => normalizeTeamIds(user?.teamIds, user?.primaryTeamId),
    [user]
  )

  const agentTeams = useMemo(
    () => availableTeams.filter(team => agentTeamIds.includes(team.id)),
    [availableTeams, agentTeamIds]
  )

  const [historyFilterValue, setHistoryFilterValue] =
    useState<HistoryFilterValue>('teams')

  const selectedTeamId = useMemo(() => {
    if (!historyFilterValue.startsWith('team:')) {
      return null
    }
    const numeric = Number.parseInt(historyFilterValue.split(':')[1], 10)
    return Number.isNaN(numeric) ? null : numeric
  }, [historyFilterValue])

  useEffect(() => {
    if (!showHistory) {
      return
    }
    if (agentTeamIds.length > 0 && availableTeams.length === 0) {
      fetchTeams().catch(error => {
        console.error('Failed to fetch teams for handover history:', error)
      })
    }
  }, [showHistory, agentTeamIds.length, availableTeams.length, fetchTeams])

  useEffect(() => {
    if (historyFilterValue === 'teams' && agentTeamIds.length === 0) {
      setHistoryFilterValue('me')
      return
    }

    if (
      historyFilterValue.startsWith('team:') &&
      (selectedTeamId === null || !agentTeamIds.includes(selectedTeamId))
    ) {
      setHistoryFilterValue(agentTeamIds.length > 0 ? 'teams' : 'me')
    }
  }, [historyFilterValue, agentTeamIds, selectedTeamId])

  const historyQueryOptions = useMemo<HistoryQueryOptions>(() => {
    if (!showHistory) {
      return { scope: 'me' }
    }

    if (historyFilterValue === 'me' || agentTeamIds.length === 0) {
      return { scope: 'me' }
    }

    if (
      historyFilterValue.startsWith('team:') &&
      selectedTeamId !== null &&
      agentTeamIds.includes(selectedTeamId)
    ) {
      return { scope: 'team', teamId: selectedTeamId }
    }

    return { scope: 'teams' }
  }, [showHistory, historyFilterValue, agentTeamIds, selectedTeamId])

  const historyScopeKey =
    historyQueryOptions.scope === 'team'
      ? `team-${historyQueryOptions.teamId}`
      : historyQueryOptions.scope

  const pendingAssignmentsKey = useMemo(
    () =>
      mode === 'pendingRequests'
        ? ([
            'agent',
            'handover',
            'pending',
            'requests',
            teamId ?? 'all',
          ] as const)
        : (['agent', 'handover', 'pending', 'assignments'] as const),
    [mode, teamId]
  )

  const handoverHistoryKey = useMemo(
    () => ['agent', 'handover', 'history', historyScopeKey] as const,
    [historyScopeKey]
  )

  const {
    data: pendingAssignmentsData,
    isFetching: pendingAssignmentsLoading,
    error: pendingError,
  } = useQuery({
    queryKey: pendingAssignmentsKey,
    refetchInterval: 30000,
    queryFn: async () => {
      try {
        if (mode === 'pendingRequests') {
          const response = await api.sessions.getPendingHandovers(
            teamId != null ? teamId : undefined
          )
          if (!response.success || !Array.isArray(response.data)) {
            throw new Error(
              response.error || 'Failed to load pending handover requests'
            )
          }
          return (response.data as HandoverRequest[]).filter(r => {
            const normalized =
              typeof r.status === 'string' ? r.status.toUpperCase() : ''
            return normalized === 'PENDING'
          })
        }
        const response = await api.agent.getPendingHandoverAssignments()
        if (!response.success || !Array.isArray(response.data)) {
          throw new Error(
            response.error || 'Failed to load handover assignments'
          )
        }
        // Filter strictly to pending requests (exclude already assigned/completed)
        return (response.data as HandoverRequest[]).filter(r => {
          const normalized =
            typeof r.status === 'string' ? r.status.toUpperCase() : ''
          return normalized === 'PENDING'
        })
      } catch (error) {
        console.error('Error fetching pending handover data:', error)
        throw error instanceof Error
          ? error
          : new Error('Failed to load handover data')
      }
    },
  })

  const {
    data: handoverHistoryData,
    isFetching: handoverHistoryLoading,
    error: historyError,
  } = useQuery({
    queryKey: handoverHistoryKey,
    refetchInterval: showHistory ? 30000 : false,
    enabled: showHistory,
    queryFn: async () => {
      const response = await api.agent.getHandoverHistory(historyQueryOptions)
      if (!response.success || !Array.isArray(response.data)) {
        throw new Error(response.error || 'Failed to load handover history')
      }
      return response.data as HandoverRequest[]
    },
  })

  const pendingAssignments = pendingAssignmentsData ?? []
  const handoverHistory = handoverHistoryData ?? []
  const isLoading = pendingAssignmentsLoading || handoverHistoryLoading
  const agentTeamNames = useMemo(
    () =>
      agentTeams
        .map(team => team.name?.trim())
        .filter((name): name is string => Boolean(name)),
    [agentTeams]
  )

  const selectedTeamName = useMemo(() => {
    if (selectedTeamId === null) {
      return null
    }
    const match = agentTeams.find(team => team.id === selectedTeamId)
    return match?.name?.trim() || null
  }, [agentTeams, selectedTeamId])

  const historyFilterDescription = useMemo(() => {
    if (historyQueryOptions.scope === 'me') {
      return 'Showing requests that are assigned to you.'
    }
    if (historyQueryOptions.scope === 'team') {
      return selectedTeamName
        ? `Showing requests handled by ${selectedTeamName}.`
        : 'Showing requests for the selected team.'
    }
    if (agentTeamNames.length === 0) {
      return 'Showing requests for your teams.'
    }
    if (agentTeamNames.length === 1) {
      return `Showing requests handled by ${agentTeamNames[0]}.`
    }
    return `Showing requests across ${agentTeamNames.length} teams.`
  }, [historyQueryOptions.scope, selectedTeamName, agentTeamNames])

  const historyEmptyState = useMemo(() => {
    if (historyQueryOptions.scope === 'me') {
      return {
        title: 'No requests',
        description: "You haven't been assigned any handover requests yet.",
      }
    }
    if (historyQueryOptions.scope === 'team') {
      return {
        title: 'No team requests',
        description: selectedTeamName
          ? `No recent requests for ${selectedTeamName}.`
          : 'No recent requests for the selected team.',
      }
    }
    return {
      title: 'No team requests',
      description:
        agentTeamNames.length > 0
          ? 'No recent requests for your teams.'
          : 'No recent requests available.',
    }
  }, [historyQueryOptions.scope, selectedTeamName, agentTeamNames.length])

  const activeTeamBadges = useMemo(() => {
    if (historyQueryOptions.scope === 'team' && selectedTeamName) {
      return [selectedTeamName]
    }
    if (historyQueryOptions.scope === 'teams') {
      return agentTeamNames
    }
    return []
  }, [historyQueryOptions.scope, selectedTeamName, agentTeamNames])

  const handleHistoryFilterChange = useCallback((value: string) => {
    if (value === 'teams' || value === 'me' || value.startsWith('team:')) {
      setHistoryFilterValue(value as HistoryFilterValue)
    }
  }, [])
  const responseModalTeamMeta = useMemo(() => {
    if (!responseModalData) {
      return { teamIds: [] as number[], primaryTeamId: null as number | null }
    }
    return resolveTeamMeta({
      teamIds: (responseModalData.request as any)?.teamIds,
      primaryTeamId: (responseModalData.request as any)?.primaryTeamId,
      teamId: responseModalData.request.teamId,
    })
  }, [responseModalData])

  const responseModalAgentTeamMeta = useMemo(() => {
    if (!responseModalData?.request?.agent) {
      return { teamIds: [] as number[], primaryTeamId: null as number | null }
    }
    return resolveTeamMeta(responseModalData.request.agent as any)
  }, [responseModalData])

  useEffect(() => {
    if (pendingError instanceof Error) {
      console.error('Error loading handover assignments:', pendingError)
      setError('Failed to load handover assignments')
    }
  }, [pendingError])

  useEffect(() => {
    if (historyError instanceof Error) {
      console.error('Error loading handover history:', historyError)
      setError('Failed to load handover history')
    }
  }, [historyError])

  useEffect(() => {
    if (!pendingError && !historyError) {
      setError(null)
    }
  }, [pendingError, historyError])

  const openResponseModal = (
    request: HandoverRequest,
    response: 'accepted' | 'rejected'
  ) => {
    if (!canRespondToTransfers) {
      setError('You do not have permission to respond to handovers.')
      return
    }
    setResponseModalData({
      request,
      response,
      reason: '',
    })
    setShowResponseModal(true)
  }

  const handleOpenChat = (sessionId?: string | null) => {
    if (!sessionId) {
      console.warn('No sessionId provided, cannot open chat')
      return
    }

    router.push(buildAgentDashboardChatUrl(sessionId))
  }

  const handleDirectAccept = async (request: HandoverRequest) => {
    if (!canRespondToTransfers) {
      setError('You do not have permission to accept handovers.')
      return
    }

    // Only show team selection modal if:
    // 1. Handover is broadcast (no team_id assigned on handover_requests)
    // 2. AND agent has multiple teams
    const isBroadcastHandover = !request.teamId
    if (isBroadcastHandover && agentTeamIds.length > 1) {
      // Show team selection modal for broadcast handovers
      setPendingAcceptRequest(request)
      // Default to agent's first team
      setSelectedTeamForAccept(agentTeamIds[0])
      setShowTeamSelectionModal(true)
      return
    }

    // If handover already has team_id OR agent has single/no teams - proceed directly
    const teamIdToUse = request.teamId || agentTeamIds[0] || null
    await performAcceptHandover(request, teamIdToUse)
  }

  const performAcceptHandover = async (
    request: HandoverRequest,
    teamId: number | null
  ) => {
    try {
      setIsSubmitting(true)

      console.log('🔄 [DEBUG] Direct accepting handover:', {
        requestId: request.id,
        sessionId: request.sessionId,
        teamId,
      })

      await api.agent.respondToHandoverAssignment(request.id, {
        response: 'accepted',
        reason: '',
        teamId: teamId || undefined,
      })

      console.log('✅ [DEBUG] Handover accepted successfully')

      const sessionId = request.sessionId

      console.log('✅ [DEBUG] Handover accepted, session ID:', sessionId)

      // Show success notification
      showSuccess(
        'Handover Accepted',
        `You are now handling session ${sessionId?.slice(-8) || ''}`
      )

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: pendingAssignmentsKey }),
        queryClient.invalidateQueries({ queryKey: handoverHistoryKey }),
      ])
      onUpdate?.()

      if (sessionId) {
        console.log('🚀 [DEBUG] Redirecting to chat in 500ms...')
        setTimeout(() => {
          console.log('🚀 [DEBUG] Redirecting to chat now...')
          router.push(buildAgentDashboardChatUrl(sessionId))
        }, 500)
      } else {
        console.error('❌ [DEBUG] No sessionId found, trying fallback approach')

        // Fallback: try to find newly assigned session
        setTimeout(async () => {
          try {
            const activeResponse = await api.sessions.getActiveSessions()
            if (activeResponse.success && activeResponse.data) {
              const newlyAssignedSession = activeResponse.data.find(
                (s: any) => {
                  const sessionStatus =
                    typeof s.status === 'string' ? s.status.toUpperCase() : null
                  return sessionStatus === 'ASSIGNED' || s.assignedTo
                }
              )

              if (newlyAssignedSession) {
                console.log(
                  '🎯 [DEBUG] Found newly assigned session via fallback:',
                  newlyAssignedSession.id
                )
                router.push(
                  buildAgentDashboardChatUrl(String(newlyAssignedSession.id))
                )
              } else {
                console.error(
                  '❌ [DEBUG] Could not find newly assigned session'
                )
              }
            }
          } catch (error) {
            console.error('❌ [DEBUG] Error in fallback approach:', error)
          }
        }, 1000)
      }
    } catch (err) {
      console.error('Error accepting handover:', err)
      setError('Failed to accept handover')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmitResponse = async () => {
    if (!canRespondToTransfers) {
      setError('You do not have permission to respond to handovers.')
      return
    }
    if (!responseModalData) return

    // If accepting and agent has multiple teams, show team selection
    if (responseModalData.response === 'accepted' && agentTeamIds.length > 1) {
      setShowResponseModal(false)
      setPendingAcceptRequest(responseModalData.request)
      setSelectedTeamForAccept(agentTeamIds[0])
      setShowTeamSelectionModal(true)
      setResponseModalData(null)
      return
    }

    // Single team or rejecting - proceed directly
    const teamId =
      responseModalData.response === 'accepted' ? agentTeamIds[0] || null : null

    try {
      setIsSubmitting(true)

      console.log('🔄 [DEBUG] Submitting handover response:', {
        requestId: responseModalData.request.id,
        response: responseModalData.response,
        sessionId: responseModalData.request.sessionId,
        teamId,
      })

      await api.agent.respondToHandoverAssignment(
        responseModalData.request.id,
        {
          response: responseModalData.response,
          reason: responseModalData.reason,
          teamId: teamId || undefined,
        }
      )

      console.log('✅ [DEBUG] Handover response submitted successfully')

      setShowResponseModal(false)

      // If accepting, show success notification and redirect to chat
      if (responseModalData.response === 'accepted') {
        const sessionId = responseModalData.request.sessionId

        console.log('✅ [DEBUG] Handover accepted, session ID:', sessionId)

        // Show success notification
        showSuccess(
          'Handover Accepted',
          `You are now handling session ${sessionId?.slice(-8) || ''}`
        )

        // Close modal and redirect
        setResponseModalData(null)

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: pendingAssignmentsKey }),
          queryClient.invalidateQueries({ queryKey: handoverHistoryKey }),
        ])
        onUpdate?.()

        if (sessionId) {
          console.log('🚀 [DEBUG] Redirecting to chat in 500ms...')
          setTimeout(() => {
            console.log('🚀 [DEBUG] Redirecting to chat now...')
            router.push(buildAgentDashboardChatUrl(sessionId))
          }, 500)
        } else {
          console.error(
            '❌ [DEBUG] No sessionId found, trying fallback approach'
          )

          // Fallback: try to find newly assigned session
          setTimeout(async () => {
            try {
              const activeResponse = await api.sessions.getActiveSessions()
              if (activeResponse.success && activeResponse.data) {
                const newlyAssignedSession = activeResponse.data.find(
                  (s: any) => {
                    const sessionStatus =
                      typeof s.status === 'string'
                        ? s.status.toUpperCase()
                        : null
                    return sessionStatus === 'ASSIGNED' || s.assignedTo
                  }
                )

                if (newlyAssignedSession) {
                  console.log(
                    '🎯 [DEBUG] Found newly assigned session via fallback:',
                    newlyAssignedSession.id
                  )
                  router.push(
                    buildAgentDashboardChatUrl(String(newlyAssignedSession.id))
                  )
                } else {
                  console.error(
                    '❌ [DEBUG] Could not find newly assigned session'
                  )
                }
              }
            } catch (error) {
              console.error('❌ [DEBUG] Error in fallback approach:', error)
            }
          }, 1000)
        }
      } else {
        // For rejections, just close modal and refresh
        setResponseModalData(null)
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: pendingAssignmentsKey }),
          queryClient.invalidateQueries({ queryKey: handoverHistoryKey }),
        ])
        onUpdate?.()
      }
    } catch (err) {
      console.error('Error submitting response:', err)
      setError('Failed to submit response')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getStatusColor = (status: string) => {
    const normalized = typeof status === 'string' ? status.toUpperCase() : ''
    switch (normalized) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'ASSIGNED':
        return 'bg-blue-100 text-blue-800'
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800'
      case 'REJECTED':
        return 'bg-red-100 text-red-800'
      case 'COMPLETED':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleConfirmTeamSelection = async () => {
    if (!pendingAcceptRequest || selectedTeamForAccept === null) {
      return
    }

    setShowTeamSelectionModal(false)
    await performAcceptHandover(pendingAcceptRequest, selectedTeamForAccept)
    setPendingAcceptRequest(null)
    setSelectedTeamForAccept(null)
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'high':
        return 'bg-orange-100 text-orange-800'
      case 'normal':
        return 'bg-blue-100 text-blue-800'
      case 'low':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    const normalized = typeof status === 'string' ? status.toUpperCase() : ''
    switch (normalized) {
      case 'PENDING':
        return <Clock className="h-4 w-4" />
      case 'ASSIGNED':
        return <User className="h-4 w-4" />
      case 'ACCEPTED':
        return <UserCheck className="h-4 w-4" />
      case 'REJECTED':
        return <UserX className="h-4 w-4" />
      case 'COMPLETED':
        return <CheckCircle className="h-4 w-4" />
      default:
        return <Bell className="h-4 w-4" />
    }
  }

  // NOTE: use shared formatRelativeTime for compact 'X ago' UI

  if (isLoading) {
    return (
      <div className={`${className} bg-white rounded-lg shadow p-6`}>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mr-3" />
          <span className="text-gray-600">Loading notifications...</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`${className} space-y-6`}>
      {/* Pending Assignments */}
      {showPendingAssignments && pendingAssignments.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Bell className="h-6 w-6 text-orange-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Pending Requests ({pendingAssignments.length})
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Handover requests and transfer requests awaiting response
                  </p>
                </div>
              </div>
              <button
                onClick={async () => {
                  setError(null)
                  await Promise.all([
                    queryClient.invalidateQueries({
                      queryKey: pendingAssignmentsKey,
                    }),
                    queryClient.invalidateQueries({
                      queryKey: handoverHistoryKey,
                    }),
                  ])
                }}
                disabled={isLoading}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {/* Show ALL pending requests - both assigned and unassigned */}
            {pendingAssignments.map(assignment => {
              const { primaryTeamId: assignmentPrimaryTeamId } =
                resolveTeamMeta({
                  teamIds: (assignment as any)?.teamIds,
                  primaryTeamId: (assignment as any)?.primaryTeamId,
                  teamId: assignment.teamId,
                })
              const { primaryTeamId: agentPrimaryTeamId } = resolveTeamMeta(
                assignment.agent ?? {}
              )
              const platform =
                assignment.sessionPlatform &&
                assignment.sessionPlatform.trim().length > 0
                  ? assignment.sessionPlatform.trim()
                  : 'web'
              const platformLabel = getPlatformLabel(platform)
              const platformId =
                assignment.sessionPlatformId &&
                assignment.sessionPlatformId.trim().length > 0
                  ? assignment.sessionPlatformId.trim()
                  : null

              return (
                <div key={assignment.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">
                            Chat Session{' '}
                            {assignment.sessionId?.slice(-8) || 'unknown'}
                            {assignment.requestedBy === 'agent' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                <User className="h-3 w-3 mr-1" />
                                Agent Transfer
                              </span>
                            )}
                          </h4>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-[11px] font-semibold"
                            >
                              <span className="flex items-center gap-1">
                                {getPlatformIcon(platform)}
                                {platformLabel}
                              </span>
                            </Badge>
                            {platformId && (
                              <span className="text-xs text-gray-500">
                                Channel ID: {platformId}
                              </span>
                            )}
                          </div>
                          {assignment.reason && (
                            <p className="text-sm text-gray-600 mt-1">
                              {assignment.reason}
                            </p>
                          )}
                          {assignment.requestedBy === 'agent' &&
                            assignment.assignedBy && (
                              <p className="text-sm text-blue-600 mt-1 font-medium">
                                Transfer request from Agent ID:{' '}
                                {assignment.assignedBy}
                              </p>
                            )}

                          {/* Assigned Agent Information */}
                          {assignment.agent && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-medium text-gray-700">
                                    Assigned Agent:{' '}
                                  </span>
                                  <span className="text-gray-600">
                                    {assignment.agent.firstName &&
                                    assignment.agent.lastName
                                      ? `${assignment.agent.firstName} ${assignment.agent.lastName}`
                                      : assignment.agent.username}
                                  </span>
                                  {agentPrimaryTeamId != null && (
                                    <span className="text-gray-500 ml-2">
                                      (Team {agentPrimaryTeamId})
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      assignment.agent.isOnline
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    {assignment.agent.isOnline
                                      ? '🟢 Online'
                                      : '🔴 Offline'}
                                  </span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {assignment.agent.role}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="flex items-center mt-2 space-x-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(assignment.priority)}`}
                            >
                              {assignment.priority}
                            </span>
                            {assignment.teamName && (
                              <span className="text-xs text-gray-500">
                                Team: {assignment.teamName}
                              </span>
                            )}
                            {assignmentPrimaryTeamId != null && (
                              <span className="text-xs text-gray-500">
                                Team ID: {assignmentPrimaryTeamId}
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              {assignment.requestedBy === 'agent'
                                ? 'Transfer requested'
                                : 'Assigned'}
                              :{' '}
                              {formatRelativeTime(
                                assignment.assignedAt || assignment.requestedAt
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-3 ml-6">
                      <button
                        onClick={() => handleOpenChat(assignment.sessionId)}
                        disabled={!assignment.sessionId}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Open Chat
                      </button>
                      {(() => {
                        // Acceptance gating logic
                        const status = assignment.status
                        const normalizedStatus =
                          typeof status === 'string' ? status.toUpperCase() : ''
                        const assignedTo = assignment.assignedTo
                        const teamName = assignment.teamName
                          ?.toLowerCase()
                          .trim()
                        const teamId = assignment.teamId
                        const agentId = user?.id // Fixed: use user.id instead of user.agentId
                        const teamNameSet = new Set(
                          agentTeamNames.map(n => n.toLowerCase().trim())
                        )

                        // A team name is considered eligible if it matches one of the agent's team names.
                        const teamNameMatches =
                          !teamName || teamNameSet.has(teamName)
                        // Team constraint: if the request carries a teamId, agent must belong to that team.
                        const teamMatches =
                          teamId == null || agentTeamIds.includes(teamId)

                        // Status rules:
                        // - pending & unassigned: any eligible team agent may accept
                        // - pending & assignedTo = current agent: agent may accept
                        // - assigned & assignedTo = current agent: agent may accept
                        // All other cases: deny.
                        const isAssignedToAgent =
                          assignedTo != null && agentId === assignedTo
                        const isPending = normalizedStatus === 'PENDING'
                        const isAssignedStatus = normalizedStatus === 'ASSIGNED'

                        const statusEligible =
                          (isPending &&
                            (assignedTo == null || isAssignedToAgent)) ||
                          (isAssignedStatus && isAssignedToAgent)

                        const acceptAllowed =
                          canRespondToTransfers &&
                          teamNameMatches &&
                          teamMatches &&
                          statusEligible &&
                          !isSubmitting

                        return (
                          <button
                            onClick={() => handleDirectAccept(assignment)}
                            disabled={!acceptAllowed}
                            title={
                              acceptAllowed
                                ? 'Accept this handover request'
                                : !canRespondToTransfers
                                  ? 'You lack CHAT_TRANSFER permission'
                                  : !teamNameMatches
                                    ? 'Team name not in your teams'
                                    : !teamMatches
                                      ? 'Team mismatch'
                                      : !statusEligible
                                        ? 'Request not eligible for acceptance'
                                        : 'Disabled'
                            }
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-green-600 hover:bg-green-700 focus:ring-green-500"
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Accept
                          </button>
                        )
                      })()}
                      {/* <button
                        onClick={() =>
                          openResponseModal(assignment, 'rejected')
                        }
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <UserX className="h-4 w-4 mr-1" />
                        Reject
                      </button> */}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Handover History */}
      {showHistory && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center">
                  <MessageSquare className="h-6 w-6 text-blue-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Recent Chat Requests
                  </h3>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  {historyFilterDescription}
                </p>
                {activeTeamBadges.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {activeTeamBadges.map(name => (
                      <Badge
                        key={name}
                        variant="outline"
                        className="text-xs font-medium"
                      >
                        {name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 sm:items-end">
                <span className="text-xs font-medium uppercase text-gray-500">
                  Filter
                </span>
                <Select
                  value={historyFilterValue}
                  onValueChange={handleHistoryFilterChange}
                >
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Select filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      value="teams"
                      disabled={agentTeamIds.length === 0}
                    >
                      All My Teams
                    </SelectItem>
                    <SelectItem value="me">Only My Requests</SelectItem>
                    {agentTeams.map(team => (
                      <SelectItem key={team.id} value={`team:${team.id}`}>
                        Team: {team.name?.trim() || `#${team.id}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {handoverHistory.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
              <h4 className="mt-2 text-sm font-medium text-gray-900">
                {historyEmptyState.title}
              </h4>
              <p className="mt-1 text-sm text-gray-500">
                {historyEmptyState.description}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {handoverHistory.slice(0, 10).map(item => (
                <div key={item.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">
                            Session {item.sessionId?.slice(-8) || 'unknown'}
                          </h4>
                          {item.reason && (
                            <p className="text-sm text-gray-600 mt-1">
                              {item.reason}
                            </p>
                          )}
                          <div className="flex items-center mt-2 space-x-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}
                            >
                              {getStatusIcon(item.status)}
                              <span className="ml-1">{item.status}</span>
                            </span>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(item.priority)}`}
                            >
                              {item.priority}
                            </span>
                            {item.teamName && (
                              <span className="text-xs text-gray-500">
                                {item.teamName}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Requested: {formatRelativeTime(item.requestedAt)}
                            {item.agentResponseAt && (
                              <span className="ml-4">
                                Responded:{' '}
                                {formatRelativeTime(item.agentResponseAt)}
                              </span>
                            )}
                          </div>
                          {item.agentResponseReason && (
                            <p className="text-xs text-gray-600 mt-1">
                              Response reason: {item.agentResponseReason}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Response Modal */}
      <Dialog
        open={showResponseModal}
        onOpenChange={open => {
          setShowResponseModal(open)
          if (!open) {
            setResponseModalData(null)
            setIsSubmitting(false)
          }
        }}
      >
        <DialogContent className="max-w-md">
          {responseModalData ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {responseModalData.response === 'accepted'
                    ? 'Accept'
                    : 'Reject'}{' '}
                  Handover Assignment
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="session-id">Session ID</Label>
                  <input
                    id="session-id"
                    type="text"
                    value={responseModalData.request.sessionId || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                {/* Team Information */}
                {responseModalTeamMeta.primaryTeamId != null && (
                  <div className="space-y-2">
                    <Label>Team Information</Label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span className="text-sm text-gray-700">
                          Team ID: {responseModalTeamMeta.primaryTeamId}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Request Details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Request Details
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Priority:</span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(responseModalData.request.priority)}`}
                      >
                        {responseModalData.request.priority}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Requested by:
                      </span>
                      <span className="text-sm text-gray-800 font-medium">
                        {responseModalData.request.requestedBy}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Requested:</span>
                      <span className="text-sm text-gray-800">
                        {formatRelativeTime(
                          responseModalData.request.requestedAt
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Current Assigned Agent (if any) */}
                {responseModalData.request.agent && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currently Assigned Agent
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                responseModalData.request.agent.isOnline
                                  ? 'bg-green-500'
                                  : 'bg-red-500'
                              }`}
                            ></div>
                            <span className="text-sm text-gray-800 font-medium">
                              {responseModalData.request.agent.firstName &&
                              responseModalData.request.agent.lastName
                                ? `${responseModalData.request.agent.firstName} ${responseModalData.request.agent.lastName}`
                                : responseModalData.request.agent.username}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                              {responseModalData.request.agent.role}
                            </span>
                            {responseModalAgentTeamMeta.primaryTeamId !=
                              null && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Team {responseModalAgentTeamMeta.primaryTeamId}
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          className={`text-xs px-2 py-1 rounded-full ${
                            responseModalData.request.agent.isOnline
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {responseModalData.request.agent.isOnline
                            ? 'Online'
                            : 'Offline'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {responseModalData.request.reason && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Original Reason
                    </label>
                    <textarea
                      value={responseModalData.request.reason}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                      rows={3}
                    />
                  </div>
                )}

                {responseModalData.response === 'rejected' && (
                  <div className="space-y-2">
                    <Label htmlFor="rejection-reason">
                      Reason for rejection *
                    </Label>
                    <Textarea
                      id="rejection-reason"
                      value={responseModalData.reason}
                      onChange={e =>
                        setResponseModalData({
                          ...responseModalData,
                          reason: e.target.value,
                        })
                      }
                      placeholder="Please provide a reason for rejecting this assignment..."
                      rows={3}
                      required
                      disabled={!canRespondToTransfers}
                    />
                  </div>
                )}

                {responseModalData.response === 'accepted' && (
                  <div className="space-y-2">
                    <Label htmlFor="acceptance-notes">
                      Additional Notes (Optional)
                    </Label>
                    <Textarea
                      id="acceptance-notes"
                      value={responseModalData.reason}
                      onChange={e =>
                        setResponseModalData({
                          ...responseModalData,
                          reason: e.target.value,
                        })
                      }
                      placeholder="Any additional notes about accepting this assignment..."
                      rows={3}
                      disabled={!canRespondToTransfers}
                    />
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResponseModal(false)
                    setResponseModalData(null)
                    setIsSubmitting(false)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitResponse}
                  disabled={
                    isSubmitting ||
                    !canRespondToTransfers ||
                    (responseModalData.response === 'rejected' &&
                      !responseModalData.reason.trim())
                  }
                  variant={
                    responseModalData.response === 'accepted'
                      ? 'default'
                      : 'destructive'
                  }
                >
                  {isSubmitting
                    ? 'Submitting...'
                    : responseModalData.response === 'accepted'
                      ? 'Accept Assignment'
                      : 'Reject Assignment'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-6 text-sm text-gray-500">
              Select a handover request to respond.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Selection Modal */}
      <Dialog
        open={showTeamSelectionModal}
        onOpenChange={setShowTeamSelectionModal}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Select Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              This is a broadcast handover (no specific team assigned). Please
              select which team you want to accept this handover under:
            </p>
            <div className="space-y-2">
              <Label htmlFor="team-select">Team</Label>
              <Select
                value={selectedTeamForAccept?.toString() || ''}
                onValueChange={value => setSelectedTeamForAccept(Number(value))}
              >
                <SelectTrigger id="team-select">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {agentTeams.map(team => (
                    <SelectItem key={team.id} value={team.id.toString()}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {pendingAcceptRequest && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md space-y-2">
                <div className="text-xs text-gray-500">Session</div>
                <div className="text-sm font-medium">
                  {pendingAcceptRequest.sessionId?.slice(-8) || 'Unknown'}
                </div>
                {pendingAcceptRequest.sessionPlatform && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Badge variant="outline" className="text-xs">
                      {getPlatformLabel(pendingAcceptRequest.sessionPlatform)}
                    </Badge>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowTeamSelectionModal(false)
                setPendingAcceptRequest(null)
                setSelectedTeamForAccept(null)
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTeamSelection}
              disabled={isSubmitting || selectedTeamForAccept === null}
            >
              {isSubmitting ? 'Accepting...' : 'Accept Handover'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default HandoverNotifications
